import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Shield, Check, X } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function RolePermissions() {
  const { user } = useAuth();
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [changes, setChanges] = useState({});

  const features = [
    { key: 'daily_reports', label: 'Daily Reports' },
    { key: 'disciplinary', label: 'Disciplinary' },
    { key: 'job_applications', label: 'Job Applications' },
    { key: 'users', label: 'User Management' },
    { key: 'settings', label: 'Settings' }
  ];

  const roles = [
    { key: 'teacher', label: 'Teacher' },
    { key: 'staff', label: 'Staff' },
    { key: 'board_member', label: 'Board Member' },
    { key: 'student', label: 'Student' }
  ];

  useEffect(() => {
    fetchPermissions();
  }, []);

  const fetchPermissions = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/permissions`, {
        withCredentials: true,
      });
      setPermissions(data);
    } catch (error) {
      console.error('Error fetching permissions:', error);
      toast.error('Failed to load permissions');
    } finally {
      setLoading(false);
    }
  };

  const getPermission = (role, feature) => {
    const perm = permissions.find(p => p.role === role && p.feature === feature);
    return perm || { can_view: false, can_edit: false };
  };

  const togglePermission = (role, feature, type) => {
    const key = `${role}-${feature}-${type}`;
    const currentPerm = getPermission(role, feature);
    const newValue = type === 'view' ? !currentPerm.can_view : !currentPerm.can_edit;

    // Update local state
    const updatedPerms = [...permissions];
    const existingIndex = updatedPerms.findIndex(p => p.role === role && p.feature === feature);
    
    if (existingIndex >= 0) {
      updatedPerms[existingIndex] = {
        ...updatedPerms[existingIndex],
        [type === 'view' ? 'can_view' : 'can_edit']: newValue
      };
    } else {
      updatedPerms.push({
        role,
        feature,
        can_view: type === 'view' ? newValue : false,
        can_edit: type === 'edit' ? newValue : false
      });
    }

    setPermissions(updatedPerms);
    setChanges({ ...changes, [key]: true });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${BACKEND_URL}/api/permissions`,
        permissions,
        { withCredentials: true }
      );
      toast.success('Permissions updated successfully');
      setChanges({});
      fetchPermissions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update permissions');
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = Object.keys(changes).length > 0;

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div data-testid="role-permissions-page">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-[#0F172A] tracking-tight" style={{ fontFamily: 'Manrope' }}>
            Role Permissions
          </h1>
          <p className="text-[#64748B] mt-2">Configure what each role can view and edit</p>
        </div>
        {hasChanges && (
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-gradient-to-r from-[#667EEA] to-[#764BA2] hover:from-[#764BA2] hover:to-[#667EEA]"
            data-testid="save-permissions-btn"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-[#667EEA]" />
            Permission Matrix
          </CardTitle>
          <CardDescription>
            Admin and Board Member roles have full access by default (not shown below)
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-8">
            {roles.map(role => (
              <div key={role.key} className="space-y-4">
                <h3 className="text-lg font-semibold text-[#0F172A]">{role.label}</h3>
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-1/3">Feature</TableHead>
                        <TableHead className="text-center">Can View</TableHead>
                        <TableHead className="text-center">Can Edit</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {features.map(feature => {
                        const perm = getPermission(role.key, feature.key);
                        return (
                          <TableRow key={feature.key}>
                            <TableCell className="font-medium">{feature.label}</TableCell>
                            <TableCell className="text-center">
                              <button
                                onClick={() => togglePermission(role.key, feature.key, 'view')}
                                className={`p-2 rounded-md transition-colors ${
                                  perm.can_view
                                    ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                    : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                                }`}
                                data-testid={`toggle-view-${role.key}-${feature.key}`}
                              >
                                {perm.can_view ? (
                                  <Check className="w-5 h-5" />
                                ) : (
                                  <X className="w-5 h-5" />
                                )}
                              </button>
                            </TableCell>
                            <TableCell className="text-center">
                              <button
                                onClick={() => togglePermission(role.key, feature.key, 'edit')}
                                className={`p-2 rounded-md transition-colors ${
                                  perm.can_edit
                                    ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                                    : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                                }`}
                                data-testid={`toggle-edit-${role.key}-${feature.key}`}
                                disabled={!perm.can_view}
                              >
                                {perm.can_edit ? (
                                  <Check className="w-5 h-5" />
                                ) : (
                                  <X className="w-5 h-5" />
                                )}
                              </button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-900">
              <strong>Note:</strong> Users need "Can View" permission before they can have "Can Edit" permission.
              Changing permissions will take effect immediately for all users with that role.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
